<script lang="ts">
/**
 * OperationBlock
 *
 * Orchestrates the operation view by wiring together the Header, OperationBlock,
 * and ResponseBlock. Forwards user interactions to the workspace event bus and
 * passes through configuration such as auth, servers, plugins, and environment.
 * This component keeps the Operation page lean by centralizing event emission
 * and prop wiring between the blocks.
 *
 * Notable behavior:
 * - Uses operation['x-scalar-method'] and operation['x-scalar-path'] to provide
 *   draft overrides for the UI when present.
 */
export default {
  name: 'OperationBlock',
}

export type OperationBlockProps = {
  /** Event bus */
  eventBus: WorkspaceEventBus
  /** Application version */
  appVersion: string
  /** Openapi document */
  document: OpenApiDocument
  /** Workspace cookies */
  workspaceCookies: XScalarCookie[]
  /** Document cookies */
  documentCookies: XScalarCookie[]
  /** Current request path */
  path: string
  /** Current request method */
  method: HttpMethodType
  /** HTTP clients */
  httpClients: AvailableClients
  /** The history for the operation */
  history?: HistoryEntry[]
  /** Client layout */
  layout: ClientLayout
  /** Currently selected server */
  server: ServerObject | null
  /** Currently selected client */
  selectedClient: WorkspaceStore['workspace']['x-scalar-default-client']
  /** Server list available for operation/document */
  servers: ServerObject[]
  /** Meta information for the server */
  serverMeta: ServerMeta
  /** Hides the client button on the header */
  hideClientButton?: boolean
  /** Client integration  */
  integration?: string | null
  /** Openapi document url for `modal` mode to open the client app */
  documentUrl?: string
  /** Client source */
  source?: 'gitbook' | 'api-reference'
  /** Operation object */
  operation: OperationObject
  /** Currently selected example key for the current operation */
  exampleKey: string
  /** Meta information for the auth update */
  authMeta: AuthMeta
  /** Document defined security schemes */
  securitySchemes: MergedSecuritySchemes
  /** Client plugins */
  plugins: ClientPlugin[]
  /** Environment list */
  environments?: string[]
  /** Currently selected environment */
  activeEnvironment?: string
  /** For environment variables in the inputs */
  environment: XScalarEnvironment
  /** The proxy URL for sending requests */
  proxyUrl: string
  /** Currently selected security */
  selectedSecurity: SelectedSecurity
  /** Currently selected security schemes */
  selectedSecuritySchemes: SecuritySchemeObjectSecret[]
  /** Security requirements */
  securityRequirements: OpenApiDocument['security']
  /** Default headers */
  defaultHeaders: Record<string, string>
  /** Selected anyOf/oneOf request-body variants keyed by schema path */
  requestBodyCompositionSelection?: Record<string, number>
  /** Subset of config options for the modal */
  options?: ModalProps['options']
}
</script>
<script setup lang="ts">
import { ERRORS } from '@scalar/helpers/errors/normalize-error'
import { isElectron } from '@scalar/helpers/general/is-electron'
import { buildSafeBodyRequest } from '@scalar/helpers/http/can-method-have-body'
import type { HttpMethod as HttpMethodType } from '@scalar/helpers/http/http-methods'
import { executeHook, type ClientPlugin } from '@scalar/oas-utils/helpers'
import {
  AVAILABLE_CLIENTS,
  type AvailableClients,
} from '@scalar/types/snippetz'
import { useToasts } from '@scalar/use-toasts'
import type { WorkspaceStore } from '@scalar/workspace-store/client'
import type { SelectedSecurity } from '@scalar/workspace-store/entities/auth'
import type { HistoryEntry } from '@scalar/workspace-store/entities/history/schema'
import type {
  AuthMeta,
  ServerMeta,
  WorkspaceEventBus,
} from '@scalar/workspace-store/events'
import {
  buildRequest,
  createVariablesStoreForRequest,
  getEnvironmentVariables,
  requestFactory,
  type MergedSecuritySchemes,
  type RequestPayload,
  type SecuritySchemeObjectSecret,
} from '@scalar/workspace-store/request-example'
import type { XScalarEnvironment } from '@scalar/workspace-store/schemas/extensions/document/x-scalar-environments'
import type { XScalarCookie } from '@scalar/workspace-store/schemas/extensions/general/x-scalar-cookies'
import type {
  OpenApiDocument,
  ServerObject,
} from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'
import type { OperationObject } from '@scalar/workspace-store/schemas/v3.1/strict/operation'
import { computed, onBeforeUnmount, onMounted, ref, toValue, watch } from 'vue'

import ViewLayout from '@/components/ViewLayout/ViewLayout.vue'
import ViewLayoutContent from '@/components/ViewLayout/ViewLayoutContent.vue'
import { harToFetchRequest } from '@/v2/blocks/operation-block/helpers/har-to-fetch-request'
import { harToFetchResponse } from '@/v2/blocks/operation-block/helpers/har-to-fetch-response'
import {
  getOperationExampleKey,
  isStreamingResponse,
  responseCache,
} from '@/v2/blocks/operation-block/helpers/response-cache'
import {
  sendRequest,
  type ResponseInstance,
} from '@/v2/blocks/operation-block/helpers/send-request'
import { validatePathParameters } from '@/v2/blocks/operation-block/helpers/validate-path-parameters'
import { generateClientOptions } from '@/v2/blocks/operation-code-sample'
import { RequestBlock } from '@/v2/blocks/request-block'
import { ResponseBlock } from '@/v2/blocks/response-block'
import { type History } from '@/v2/blocks/scalar-address-bar-block'
import type { ModalProps } from '@/v2/features/modal/Modal.vue'
import type { ClientLayout } from '@/v2/types/layout'

import Header from './components/Header.vue'

const {
  authMeta,
  environment,
  eventBus,
  exampleKey,
  document,
  workspaceCookies = [],
  documentCookies = [],
  hideClientButton,
  httpClients = AVAILABLE_CLIENTS,
  history = [],
  method,
  operation,
  path,
  plugins = [],
  proxyUrl,
  requestBodyCompositionSelection,
  securitySchemes,
  selectedClient,
  server,
  environments,
  options,
  activeEnvironment,
  serverMeta,
  selectedSecurity,
  selectedSecuritySchemes,
  securityRequirements,
  defaultHeaders,
} = defineProps<OperationBlockProps>()

/** Hoist up client generation so it doesn't get re-generated on every operation */
const clientOptions = computed(() => generateClientOptions(httpClients))

const { toast } = useToasts()

// Refs
const abortController = ref<AbortController | null>(null)
const response = ref<ResponseInstance | null>(null)
const requestPayload = ref<RequestPayload | null>(null)

/** Cancel the request */
const cancelRequest = () => abortController.value?.abort(ERRORS.REQUEST_ABORTED)

/** Execute the current operation example */
const handleExecute = async () => {
  const pathValidation = validatePathParameters(
    operation.parameters ?? [],
    exampleKey,
  )
  if (pathValidation.ok === false) {
    toast('Path parameters must have values.', 'error')
    return
  }

  const globalCookies = [...workspaceCookies, ...documentCookies]

  const { request: requestBuilder } = requestFactory({
    defaultHeaders,
    environment,
    exampleName: exampleKey,
    globalCookies,
    method,
    operation,
    path,
    proxyUrl,
    server,
    selectedSecuritySchemes,
    isElectron: isElectron(),
    requestBodyCompositionSelection,
  })

  // Stop any previous streaming response
  if (response.value && 'reader' in response.value) {
    response.value.reader.cancel()
  }

  const variablesStore = createVariablesStoreForRequest()

  // Execute the beforeRequest hook (plugins receive RequestFactory, not fetch Request)
  await executeHook(
    {
      requestBuilder,
      document,
      operation,
      variablesStore,
    },
    'beforeRequest',
    plugins,
  )

  const envVariables = {
    ...getEnvironmentVariables(environment),
    ...variablesStore.getVariables(),
  }

  // Build the fetch Request after hooks may have mutated the factory
  const requestResult = (() => {
    try {
      return {
        ok: true,
        result: buildRequest(requestBuilder, {
          envVariables,
        }),
      } as const
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      return {
        ok: false,
        error: message,
      } as const
    }
  })()

  if (requestResult.ok === false) {
    toast(requestResult.error, 'error')
    return
  }

  // Store the abort controller for cancellation
  abortController.value = requestResult.result.controller

  // Execute the hooks
  eventBus.emit('hooks:on:request:sent', {
    meta: {
      method,
      path,
      exampleKey,
    },
  })

  /** Execute the request */
  const [sendError, sendResult] = await sendRequest({
    isUsingProxy: requestResult.result.isUsingProxy,
    requestPayload: requestResult.result.requestPayload,
    plugins,
    customFetch: toValue(options)?.customFetch,
  })

  if (sendResult) {
    // Execute the responseReceived hook
    await executeHook(
      {
        response: sendResult.originalResponse.clone(),
        requestBuilder,
        request: buildSafeBodyRequest(...sendResult.requestPayload),
        document,
        operation,
        variablesStore,
      },
      'responseReceived',
      plugins,
    )
  }

  // Execute the hooks
  eventBus.emit('hooks:on:request:complete', {
    payload: sendResult
      ? {
          response: sendResult.originalResponse.clone(),
          requestPayload: sendResult.requestPayload,
          duration: sendResult.response.duration,
          timestamp: sendResult.timestamp,
        }
      : undefined,
    meta: {
      method,
      path,
      exampleKey,
    },
  })

  if (sendError) {
    // clean up the response and request
    response.value = null
    requestPayload.value = null
    abortController.value = null

    toast(sendError.message, 'error')
    return
  }

  // Store the response
  response.value = sendResult.response
  requestPayload.value = sendResult.requestPayload

  // Cache non-streaming responses so they can be restored when navigating back
  if (!isStreamingResponse(sendResult.response)) {
    responseCache.set(getOperationExampleKey(method, path, exampleKey), {
      response: sendResult.response,
      requestPayload: sendResult.requestPayload,
    })
  }
}

onMounted(() => {
  eventBus.on('operation:send:request:hotkey', handleExecute)
  eventBus.on('operation:cancel:request', cancelRequest)
})
onBeforeUnmount(() => {
  eventBus.off('operation:send:request:hotkey', handleExecute)
  eventBus.off('operation:cancel:request', cancelRequest)
})

const operationHistory = computed<History[]>(() =>
  history
    .map((entry) => ({
      method: entry.request.method as HttpMethodType,
      path: entry.request.url,
      duration: entry.time,
      status: entry.response.status,
    }))
    .reverse(),
)

const handleSelectHistoryItem = ({ index }: { index: number }) => {
  const transformedIndex = (history.length ?? 0) - index - 1
  const historyItem = history[transformedIndex]
  if (!historyItem) {
    return
  }

  const navigate = () =>
    eventBus.emit('ui:navigate', {
      page: 'example',
      method,
      path,
      exampleName: 'draft',
      callback: (status) => {
        // Do not replace the response if the navigation was not successful
        if (status !== 'success') {
          return
        }
        // Reconstruct the response
        const fetchResponse = harToFetchResponse({
          harResponse: historyItem.response,
          url: historyItem.request.url,
          method,
          path,
          duration: historyItem.time,
        })

        // Reconstruct the request
        const fetchRequest = harToFetchRequest({
          harRequest: historyItem.request,
        })

        // Update the response and request
        response.value = fetchResponse
        requestPayload.value = fetchRequest
      },
    })

  eventBus.emit('operation:reload:history', {
    meta: {
      path,
      method,
    },
    index: transformedIndex,
    callback: navigate,
  })
}

/**
 * When the path, method, or example key changes: save current response to
 * cache (so it can be restored when navigating back), then restore from cache
 * for the new operation or clear if no cached response. Response is only
 * cleared on page refresh or when making a new request for that operation.
 */
watch(
  [() => path, () => method, () => exampleKey],
  ([newPath, newMethod, newExampleKey]) => {
    const newKey = getOperationExampleKey(newMethod, newPath, newExampleKey)
    const cached = responseCache.get(newKey)
    if (cached) {
      response.value = cached.response
      requestPayload.value = cached.requestPayload
    } else {
      response.value = null
      requestPayload.value = null
    }

    // Cancel any in-flight request
    cancelRequest()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  // We cancel the request if the component is unmounted
  cancelRequest()
})
</script>
<template>
  <div class="bg-b-1 flex h-full flex-col">
    <div
      class="lg:min-h-header flex w-full flex-wrap items-center justify-center p-2 lg:p-0">
      <!-- Address Bar -->
      <Header
        :activeEnvironment
        :documentUrl
        :environment
        :environments
        :eventBus
        :hideClientButton
        :history="operationHistory"
        :integration
        :layout
        :method
        :path
        :server
        :serverMeta
        :servers
        :source
        @execute="handleExecute"
        @select:history:item="handleSelectHistoryItem" />
    </div>

    <ViewLayout class="border-t">
      <ViewLayoutContent class="flex-1">
        <!-- Request Section -->
        <RequestBlock
          :authMeta
          :clientOptions
          :defaultHeaders
          :documentCookies
          :environment
          :eventBus
          :exampleKey
          :layout
          :method
          :operation
          :options="toValue(options)"
          :path
          :plugins
          :proxyUrl
          :requestBodyCompositionSelection
          :securityRequirements
          :securitySchemes
          :selectedClient
          :selectedSecurity
          :selectedSecuritySchemes
          :server
          :workspaceCookies />

        <!-- Response Section -->
        <ResponseBlock
          :appVersion
          :eventBus
          :layout
          :plugins
          :requestPayload
          :response
          :totalPerformedRequests="operationHistory.length" />
      </ViewLayoutContent>
    </ViewLayout>
  </div>
</template>
